// client/src/pages/business/MyOrders.js
import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Download, Search, Filter, ChevronDown, ChevronUp, Package, Clock, History, MessageSquare } from 'lucide-react';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import StorefrontBanner, { useStorefrontContext } from '../../components/common/StorefrontBanner';
import ReorderButton from '../../components/order/ReorderButton';
import OrderTimeline from '../../components/order/OrderTimeline';
import ModificationHistorySection from '../../components/order/ModificationHistorySection';
import LightEditAuditSection from '../../components/order/LightEditAuditSection';
import ConfirmDeliveryDate from '../../components/order/ConfirmDeliveryDate';
import { ordersAPI } from '../../services/api';
import axios from 'axios';

// API Configuration
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const MyOrders = () => {
  const { t } = useTranslation(['business', 'orders']);
  const { isFromStorefront, wholesalerId: storefrontWholesalerId } = useStorefrontContext();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [expandedHistory, setExpandedHistory] = useState(new Set());
  const [commentText, setCommentText] = useState({});
  const [commentError, setCommentError] = useState({});
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [wholesalerFilter, setWholesalerFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [wholesalers, setWholesalers] = useState([]);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  // ✅ NEW: Securely Fetch & Download Invoice
  // This replaces the old getInvoiceDownloadUrl helper
  const handleDownloadInvoice = async (orderId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error(t('myOrders.notLoggedIn'));
        return;
      }

      // 1. Request the Secure Signed URL from Backend
      // We use the same route as wholesalers because the backend checks ownership for both parties
      const response = await axios.get(`${API_URL}/wholesalers/orders/${orderId}/invoice`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // 2. Open the Signed URL (Direct download from Cloudinary)
      if (response.data.success && response.data.url) {
        window.open(response.data.url, '_blank');
      } else {
        throw new Error('Invalid server response');
      }
    } catch (err) {
      console.error('Error downloading invoice:', err);
      // Handle specifically the case where the file is missing/not ready
      const msg = err.response?.data?.message || err.message || 'Unknown error';
      toast.error(`${t('myOrders.invoiceFailed')} ${msg}`);
    }
  };

  const fetchOrders = useCallback(async (pageToFetch = 1, append = false) => {
    try {
      setLoading(!append); // Only show full loading on fresh fetch
      if (append) setLoadingMore(true);
      
      const params = {
        page: pageToFetch,
        limit: 20,
        ...(statusFilter !== 'all' ? { status: statusFilter } : {})
      };
      
      const response = await ordersAPI.getMyOrders(params);
      
      const fetchedOrders = response.data.data || response.data.orders || [];
      
      if (append) {
        // Load more - append orders (prevent duplicates)
        setOrders(prev => {
          const existingIds = new Set(prev.map(o => o._id));
          const newOrders = fetchedOrders.filter(o => !existingIds.has(o._id));
          return [...prev, ...newOrders];
        });
      } else {
        // Fresh fetch - replace orders
        setOrders(fetchedOrders);
        setPage(1);
      }
      
      // Update pagination metadata
      if (response.data.pagination) {
        setPagination(response.data.pagination);
        setHasMore(response.data.pagination.page < response.data.pagination.pages);
      }
    } catch (err) {
      setError(t('myOrders.noOrders'));
      console.error(err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [statusFilter, t]);

  // Load more orders (next page)
  const loadMore = async () => {
    if (!hasMore || loadingMore) return;
    
    const nextPage = page + 1;
    setPage(nextPage);
    await fetchOrders(nextPage, true);
  };

  useEffect(() => {
    // Reset pagination when filter changes
    setPage(1);
    setHasMore(true);
    setOrders([]); // Clear orders first
    fetchOrders(1, false); // Fresh fetch, page 1, don't append
  }, [statusFilter]); // Only depend on statusFilter to avoid loops

  // Auto-apply wholesaler filter when coming from a storefront
  useEffect(() => {
    if (storefrontWholesalerId && wholesalerFilter === 'all') {
      setWholesalerFilter(storefrontWholesalerId);
    }
  }, [storefrontWholesalerId]);

  // Extract unique wholesalers when orders change
  useEffect(() => {
    if (Array.isArray(orders) && orders.length > 0) {
      const uniqueWholesalers = [...new Map(
        orders
          .filter(o => o.wholesalerId)
          .map(o => [o.wholesalerId._id, o.wholesalerId])
      ).values()];
      setWholesalers(uniqueWholesalers);
    }
  }, [orders]);

  const toggleOrderExpansion = (orderId) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  const toggleHistoryExpansion = (orderId) => {
    const newExpanded = new Set(expandedHistory);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedHistory(newExpanded);
  };

  const handleAddComment = async (orderId) => {
    const message = commentText[orderId];
    if (!message || !message.trim()) {
      setCommentError(prev => ({ ...prev, [orderId]: t('myOrders.enterComment') }));
      return;
    }
    setCommentError(prev => ({ ...prev, [orderId]: '' }));

    try {
      await ordersAPI.addComment(orderId, message);
      setCommentText({ ...commentText, [orderId]: '' });
      fetchOrders();
    } catch (err) {
      console.error('Error adding comment:', err);
      setCommentError(prev => ({ ...prev, [orderId]: t('myOrders.commentFailed') }));
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confirmed: 'bg-blue-100 text-blue-800 border-blue-200',
      processing: 'bg-blue-100 text-blue-800 border-blue-200',
      shipped: 'bg-purple-100 text-purple-800 border-purple-200',
      delivered: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: '⏳',
      confirmed: '✔️',
      processing: '⚙️',
      shipped: '🚚',
      delivered: '✅',
      cancelled: '❌'
    };
    return icons[status] || '📦';
  };

  const getOrderCardBg = (status) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-50 border-green-300 hover:border-green-400';
      case 'cancelled':
        return 'bg-red-50 border-red-300 hover:border-red-400';
      case 'confirmed':
      case 'processing':
        return 'bg-blue-50 border-blue-300 hover:border-blue-400';
      case 'shipped':
        return 'bg-purple-50 border-purple-300 hover:border-purple-400';
      case 'pending':
        return 'bg-yellow-50 border-yellow-300 hover:border-yellow-400';
      default:
        return 'bg-white border-gray-200 hover:border-gray-300';
    }
  };

  const filteredOrders = orders.filter(order => {
    // Search filter
    const matchesSearch = !searchTerm || 
      order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.wholesalerId?.companyName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    
    // Wholesaler filter
    const matchesWholesaler = wholesalerFilter === 'all' || order.wholesalerId?._id === wholesalerFilter;
    
    // Date filter
    let matchesDate = true;
    if (dateFilter !== 'all') {
      const orderDate = new Date(order.createdAt);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      switch (dateFilter) {
        case 'today':
          matchesDate = orderDate >= today;
          break;
        case 'week':
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          matchesDate = orderDate >= weekAgo;
          break;
        case 'month':
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          matchesDate = orderDate >= monthAgo;
          break;
        default:
          matchesDate = true;
      }
    }
    
    return matchesSearch && matchesStatus && matchesWholesaler && matchesDate;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex justify-center items-center h-64">
          <div className="text-xl text-gray-600">{t('myOrders.loading')}</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <StorefrontBanner />
      <main className="container mx-auto px-4 py-8 pt-16 lg:pt-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">{t('myOrders.title')}</h1>
          <p className="text-gray-600">{t('myOrders.subtitle')}</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('myOrders.searchPlaceholder') || 'Search by order # or wholesaler...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="all">{t('myOrders.allStatuses') || 'All Statuses'}</option>
                <option value="pending">{t('myOrders.statusPending') || 'Pending'}</option>
                <option value="confirmed">{t('myOrders.statusConfirmed') || 'Confirmed'}</option>
                <option value="processing">{t('myOrders.statusProcessing') || 'Processing'}</option>
                <option value="shipped">{t('myOrders.statusShipped') || 'Shipped'}</option>
                <option value="delivered">{t('myOrders.statusDelivered') || 'Delivered'}</option>
                <option value="cancelled">{t('myOrders.statusCancelled') || 'Cancelled'}</option>
              </select>
            </div>

            {/* Wholesaler Filter */}
            <select
              value={wholesalerFilter}
              onChange={(e) => setWholesalerFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">{t('myOrders.allWholesalers')}</option>
              {wholesalers.map(wholesaler => (
                <option key={wholesaler._id} value={wholesaler._id}>
                  {wholesaler.companyName}
                </option>
              ))}
            </select>

            {/* Date Filter */}
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">{t('myOrders.allTime')}</option>
              <option value="today">{t('myOrders.today')}</option>
              <option value="week">{t('myOrders.last7Days')}</option>
              <option value="month">{t('myOrders.last30Days')}</option>
            </select>
          </div>
        </div>

        {/* Orders List */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {filteredOrders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            {orders.length > 0 && (searchTerm || statusFilter !== 'all') ? (
              <>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('myOrders.noMatchingOrders', 'No matching orders')}</h3>
                <p className="text-gray-500">{t('myOrders.tryDifferentFilters', 'Try adjusting your search or filters')}</p>
                <button
                  onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}
                  className="mt-4 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                >
                  {t('myOrders.clearFilters', 'Clear filters')}
                </button>
              </>
            ) : (
              <>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('myOrders.noOrders')}</h3>
                <p className="text-gray-500">{t('myOrders.noOrdersDesc')}</p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map((order) => {
              const isExpanded = expandedOrders.has(order._id);
              const isHistoryExpanded = expandedHistory.has(order._id);
              
              return (
                <div
                  key={order._id}
                  className={`rounded-lg shadow-md border-2 transition-all duration-200 ${getOrderCardBg(order.status)}`}
                >
                  {/* Order Header */}
                  <div
                    className="p-6 cursor-pointer"
                    onClick={() => toggleOrderExpansion(order._id)}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      {/* Order Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-lg font-bold text-gray-900">
                            {order.orderNumber}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(order.status)}`}>
                            {getStatusIcon(order.status)} {t(`myOrders.status${order.status.charAt(0).toUpperCase() + order.status.slice(1)}`)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div>
                            <strong>{t('myOrders.wholesaler')}:</strong> {order.wholesalerId?.companyName || 'N/A'}
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            {format(new Date(order.createdAt), 'dd/MM/yy HH:mm')}
                          </div>
                        </div>
                      </div>

                        {/* Discount Badges in Header */}
                        {!order.wholesalerId?.hidePricing && order.catalogDiscount?.percentage > 0 && (
                          <div className="bg-blue-100 border border-blue-300 rounded-full px-3 py-1">
                            <span className="text-xs font-semibold text-blue-700">
                              {order.catalogDiscount.percentage}% {t('myOrders.catalogDiscount', 'Catalog Discount')}
                            </span>
                          </div>
                        )}
                        {!order.wholesalerId?.hidePricing && order.discount?.type !== 'none' && order.discount?.percentage > 0 && (
                          <div className="bg-green-100 border border-green-300 rounded-full px-3 py-1">
                            <span className="text-xs font-semibold text-green-700">
                              {order.discount.percentage}% {t('myOrders.customerDiscount', 'Customer Discount')}
                            </span>
                          </div>
                        )}

                      {/* Order Total */}
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-sm text-gray-600">{t('myOrders.total')}</div>
                          {order.wholesalerId?.hidePricing ? (
                            <div className="text-sm font-medium text-gray-400 italic">{t('myOrders.pricingHidden', 'Upon agreement')}</div>
                          ) : (
                            <div className="text-2xl font-bold text-gray-900">
                              €{order.totals?.total?.toFixed(2) || '0.00'}
                            </div>
                          )}
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-6 h-6 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Order Details (Expanded) */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 bg-white bg-opacity-50">
                      <div className="p-6 space-y-6">
                        {/* Order Timeline */}
                        <div className="mb-2">
                          <OrderTimeline status={order.status} isSimple={order.isSimple} />
                        </div>
                        {/* Items */}
                        <div>
                          <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                            <Package className="w-5 h-5" />
                            {t('myOrders.items', { count: order.items?.length || 0 })}
                          </h4>
                          <div className="space-y-2">
                            {order.items?.map((item, idx) => (
                              <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                                <div className="flex-1">
                                  <div className="font-medium text-gray-800">{item.name || item.productName || 'Unknown Product'}</div>
                                  <div className="text-sm text-gray-500">
                                    {order.wholesalerId?.hidePricing
                                      ? `${item.quantity} ×`
                                      : `${item.quantity} × €${(item.unitPrice || item.price || 0).toFixed(2)}`
                                    }
                                  </div>
                                </div>
                                {!order.wholesalerId?.hidePricing && (
                                  <div className="font-semibold text-gray-900">
                                    €{((item.quantity || 0) * (item.unitPrice || item.price || 0)).toFixed(2)}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Totals */}
                        {order.wholesalerId?.hidePricing ? (
                          <div className="bg-gray-50 rounded-lg p-4 text-center">
                            <p className="text-sm text-gray-500 italic">{t('myOrders.pricingConfirmedByWholesaler', 'Pricing confirmed directly by wholesaler')}</p>
                          </div>
                        ) : (
                        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">{t('myOrders.subtotal')}:</span>
                            <span className="font-medium">€{order.totals?.subtotal?.toFixed(2) || '0.00'}</span>
                          </div>
                          
                          {/* Catalog Discount Display */}
                          {order.catalogDiscount?.percentage > 0 && (
                            <>
                              <div className="flex justify-between text-sm">
                                <span className="text-blue-600 font-medium">
                                  {t('myOrders.catalogDiscount', 'Catalog Discount')} ({order.catalogDiscount.percentage}%):
                                </span>
                                <span className="font-semibold text-blue-600">
                                  -€{order.catalogDiscount.amount?.toFixed(2) || order.totals?.catalogDiscount?.toFixed(2) || '0.00'}
                                </span>
                              </div>
                              {order.catalogDiscount.names?.length > 0 && (
                                <div className="text-xs text-blue-500 ml-1">{order.catalogDiscount.names.join(', ')}</div>
                              )}
                            </>
                          )}
                          
                          {/* Customer Discount Display */}
                          {order.discount?.type !== 'none' && order.discount?.percentage > 0 && (
                            <>
                              <div className="flex justify-between text-sm">
                                <span className="text-green-600 font-medium">
                                  {t('myOrders.customerDiscount', 'Customer Discount')} ({order.discount.percentage}%):
                                </span>
                                <span className="font-semibold text-green-600">
                                  -€{order.discount.amount?.toFixed(2) || order.totals?.discount?.toFixed(2) || '0.00'}
                                </span>
                              </div>
                            </>
                          )}

                          {/* After All Discounts */}
                          {(order.catalogDiscount?.percentage > 0 || (order.discount?.type !== 'none' && order.discount?.percentage > 0)) && (
                              <div className="flex justify-between text-sm border-t border-gray-200 pt-2">
                                <span className="text-gray-600">{t('myOrders.afterDiscount')}:</span>
                                <span className="font-medium">€{order.totals?.afterDiscount?.toFixed(2) || '0.00'}</span>
                              </div>
                          )}
                          {/* ✅ REMOVED: Tax display row */}
                          <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                            <span>{t('myOrders.totalNet')}:</span>
                            <span className="text-blue-600">€{order.totals?.total?.toFixed(2) || '0.00'}</span>
                          </div>
                          {/* ✅ NEW: VAT disclaimer */}
                          <p className="text-xs text-gray-500 italic mt-2">{t('myOrders.vatDisclaimer')}</p>
                        </div>
                        )}


                          {/* Discount Savings Badge */}
                          {!order.wholesalerId?.hidePricing && ((order.catalogDiscount?.amount || 0) + (order.discount?.amount || 0)) > 0 && (
                            <div className="bg-green-100 border border-green-300 rounded-lg px-3 py-2 mt-2">
                              <p className="text-sm font-medium text-green-800 text-center">
                                ✓ {t('myOrders.youSaved')} {((order.catalogDiscount?.amount || 0) + (order.discount?.amount || 0)).toFixed(2)}€ {t('myOrders.onThisOrder')}!
                              </p>
                            </div>
                          )}

                        {/* Delivery Information */}
                        {order.delivery && (
                          <div className="bg-blue-50 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-800 mb-3">{t('myOrders.deliveryInfo')}</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">{t('myOrders.deliveryType')}:</span>
                                <span className="font-medium capitalize">{order.delivery.type || 'N/A'}</span>
                              </div>
                              <ConfirmDeliveryDate order={order} mode="business" />
                              {order.delivery.address && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">{t('myOrders.address')}:</span>
                                  <span className="font-medium text-right ml-4">{order.delivery.address}</span>
                                </div>
                              )}
                              {order.delivery.instructions && (
                                <div className="flex flex-col">
                                  <span className="text-gray-600 mb-1">{t('myOrders.instructions')}:</span>
                                  <span className="font-medium">{order.delivery.instructions}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Light Edit Audit (Simple Orders) */}
                        {order.isSimple && order.lightEditAudit?.length > 0 && (
                          <div className="border-t border-gray-200 pt-4">
                            <LightEditAuditSection entries={order.lightEditAudit} />
                          </div>
                        )}

                        {/* Modification History */}
                        {!order.isSimple && order.modificationHistory && order.modificationHistory.length > 0 && (
                          <div className="border-t border-gray-200 pt-4">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleHistoryExpansion(order._id);
                              }}
                              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                            >
                              <History className="w-5 h-5" />
                              {t('myOrders.modificationHistory')} ({order.modificationHistory.length})
                              {isHistoryExpanded ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                            
                            {isHistoryExpanded && (
                              <div className="mt-4">
                                <ModificationHistorySection order={order} hidePricing={!!order.wholesalerId?.hidePricing} />
                              </div>
                            )}
                          </div>
                        )}

                        {/* Cancellation Reason */}
                        {order.status === 'cancelled' && order.cancellationReason && (
                          <div className="border-t border-gray-200 pt-4 mb-4">
                            <h4 className="font-semibold text-gray-800 mb-3">{t('myOrders.cancellationReason')}</h4>
                            <div className="bg-red-50 rounded-lg border border-red-200 p-4 text-sm">
                              <p className="text-red-800">{order.cancellationReason}</p>
                            </div>
                          </div>
                        )}

                        {/* Comments Section */}
                        <div className="border-t border-gray-200 pt-4">
                          <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                            <MessageSquare className="w-5 h-5" />
                            {t('myOrders.comments')}
                          </h4>
                          
                          {/* Existing Comments */}
                          {order.comments && order.comments.length > 0 && (
                            <div className="space-y-3 mb-4">
                              {order.comments.map((comment, idx) => (
                                <div
                                  key={idx}
                                  className={`p-3 rounded-lg ${
                                    comment.author === 'business'
                                      ? 'bg-blue-50 border border-blue-200'
                                      : 'bg-gray-50 border border-gray-200'
                                  }`}
                                >
                                  <div className="flex justify-between items-start mb-1">
                                    <span className="font-medium text-sm text-gray-900">
                                      {comment.authorName || (comment.author === 'business' ? t('myOrders.you') : t('myOrders.wholesaler'))}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {comment.createdAt ? format(new Date(comment.createdAt), 'dd/MM/yy HH:mm') : t('myOrders.justNow')}
                                    </span>
                                  </div>
                                  <p className="text-gray-700 text-sm">{comment.message}</p>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Add Comment */}
                          <div>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={commentText[order._id] || ''}
                                onChange={(e) => {
                                  setCommentText({ ...commentText, [order._id]: e.target.value });
                                  if (commentError[order._id]) setCommentError(prev => ({ ...prev, [order._id]: '' }));
                                }}
                                placeholder={t('myOrders.addCommentPlaceholder')}
                                className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${commentError[order._id] ? 'border-red-300' : 'border-gray-300'}`}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    handleAddComment(order._id);
                                  }
                                }}
                              />
                              <button
                                onClick={() => handleAddComment(order._id)}
                                disabled={!commentText[order._id]?.trim()}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                              >
                                {t('myOrders.send')}
                              </button>
                            </div>
                            {commentError[order._id] && (
                              <p className="text-red-500 text-xs mt-1">{commentError[order._id]}</p>
                            )}
                          </div>
                        </div>

                        {/* ✅ UPDATED: Invoice Download Button */}
                        {order.invoiceUrl && (
                          <div className="border-t border-gray-200 pt-4">
                            <button
                              onClick={() => handleDownloadInvoice(order._id)}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors cursor-pointer"
                            >
                              <Download className="w-4 h-4" />
                              {t('myOrders.downloadInvoice')}
                            </button>
                          </div>
                        )}

                        {/* Reorder Button */}
                        {order.status === 'delivered' && (
                          <div className="border-t border-gray-200 pt-4">
                            <ReorderButton order={order} onSuccess={fetchOrders} />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

          {/* Load More Button */}
          {!loading && filteredOrders.length > 0 && hasMore && (
            <div className="mt-6 text-center">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium shadow-md hover:shadow-lg"
              >
                {loadingMore ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t('orders:MyOrders.loadingMore') || 'Loading more...'}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    {t('orders:MyOrders.loadMore') || 'Load More Orders'}
                    {pagination && (
                      <span className="text-sm opacity-80">
                        ({orders.length} / {pagination.total})
                      </span>
                    )}
                  </span>
                )}
              </button>
              
              {/* Pagination info */}
              {pagination && (
                <p className="mt-3 text-sm text-gray-600">
                  {t('orders:MyOrders.showingOrders') || 'Showing'} {orders.length} {t('orders:MyOrders.of') || 'of'} {pagination.total} {t('orders:MyOrders.orders') || 'orders'}
                </p>
              )}
            </div>
          )}
      </main>
      <Footer />
    </div>
  );
};

export default MyOrders;